import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import Checkbox from '../../../components/check-box'
import PropTypes from 'prop-types'
import InputBlock from '../input-block'
import { Icons } from '@/theme/Images'
import { useState } from 'react'
import { DatePickerInput } from '@/components/date-picker-input'
import { DropDownContainer } from '@/components/dropdown-container'
import { useFetchRewardsClass } from '@/pages/hooks/use-rewards-class'

const RulesForm = ({
  searchCategories = '',
  onRuleInputChange,
  onCheckboxChange,
  onSearchChangeCategory,
  groupOptions,
  formik,
}) => {
  const { setFieldValue } = formik

  const [showAdvanced, setShowAdvanced] = useState(false)

  const [selectedCategoryItems, setSelectedCategoryItems] = useState([])

  const { data: categoriesData = [], isLoading, isError, error, refetch } = useFetchRewardsClass()

  // Handle checkbox changes
  const handleCheckboxChange = (type, item) => {
    const setSelectedItems = {
      category: setSelectedCategoryItems,
    }
    const selectedItems = {
      category: selectedCategoryItems,
    }

    const updatedItems = selectedItems[type].includes(item.description)
      ? selectedItems[type].filter(desc => desc !== item.description)
      : [...selectedItems[type], item.description]

    setSelectedItems[type](updatedItems)

    setFieldValue(`${type.toLowerCase()}List`, updatedItems)

    if (onCheckboxChange) {
      onCheckboxChange(type, item.description)
    }
  }

  const filteredItems = (values, keys, searchTerm) => {
    return values.filter(item =>
      keys.some(key => item[key] && item[key].toLowerCase().includes(searchTerm.toLowerCase()))
    )
  }

  const filteredCategoryValues = filteredItems(categoriesData?.data || [], ['description'], searchCategories)
  const handleShowAdvanced = () => setShowAdvanced(!showAdvanced)

  return (
    <div>
      <div className="ml-3 flex gap-8">
        <div className="w-[384px] mb-[-40px]">
          <InputBlock
            label="Rule name*"
            className="mt-1"
            id="description"
            placeholder="Rule name"
            onChange={e => onRuleInputChange(e.target.value)}
            formik={formik}
            description="A short description. Limited to 50 characters."
          />
        </div>
        <div className="w-[384px] mb-[-40px]">
          <Label>Group</Label>
          <DropDownContainer
            key={'ruleGroup'}
            options={groupOptions}
            value={formik.values.ruleGroup}
            onChange={value => setFieldValue('ruleGroup', value)}
            placeholder={'Select type'}
            id={'ruleGroup'}
            formik={formik}
            description="Organize and prioritize rules relative to other rules within the same group."
          />
        </div>
      </div>
      {/* Recurring */}
      <div className="ml-3 flex gap-8 mt-16">
        <div className="w-[384px] mb-[-40px]">
          <DropDownContainer
            key={'recurring'}
            label="Recurring"
            options={[
              { value: 'Yes', label: 'Yes' },
              { value: 'No', label: 'No' },
            ]}
            placeholder={'Select type'}
            id={'recurring'}
            formik={formik}
            description="Will the rule be applied on a recurring basis? Ex: Quarterl"
          />
        </div>
        <div className="w-[384px] mb-[-40px]">
          <DropDownContainer
            key={'recurrencePeriod'}
            label="Recurring period"
            options={[
              { label: 'MONTHLY', value: 'MONTHLY' },
              { label: 'QUARTERLY', value: 'QUARTERLY' },
              { label: 'ANNUALLY', value: 'ANNUALLY' },
            ]}
            placeholder={'Select type'}
            id={'recurrencePeriod'}
            formik={formik}
            disabled={formik.values.recurring !== 'Yes'}
          />
        </div>
      </div>

      <div className="ml-3 flex gap-8 mt-16">
        <div className="w-[384px] mb-[-40px]">
          <DatePickerInput
            label="Start date"
            id="startDate"
            placeholder="Start date"
            autoComplete="day"
            value={formik.values.startDate}
            onChange={value => {
              const isoDate = value ? new Date(value).toISOString() : '' // Convert to ISO format
              formik.setFieldValue('startDate', isoDate)
            }}
            className={formik.errors.starDate && formik.touched.starDate ? 'border-[--error-300]' : ''}
            helpText={
              formik.touched.starDate && formik.errors.starDate ? (
                <p className="text-sm font-normal text-[--error-500]">{formik.errors.starDate}</p>
              ) : null
            }
            disableFuture={true}
          />
        </div>
        <div className="w-[384px] mb-[-40px]">
          <DatePickerInput
            label="End date"
            id="endDate"
            placeholder="End date"
            autoComplete="day"
            value={formik.values.endDate}
            onChange={value => {
              const isoDate = value ? new Date(value).toISOString() : '' // Convert to ISO format
              formik.setFieldValue('endDate', isoDate)
            }}
            className={formik.errors.endDate && formik.touched.endDate ? 'border-[--error-300]' : ''}
            helpText={
              formik.touched.endDate && formik.errors.endDate ? (
                <p className="text-sm font-normal text-[--error-500]">{formik.errors.endDate}</p>
              ) : null
            }
            disableFuture={true}
          />
        </div>
      </div>
      {/* Rule priority */}
      <div className="ml-3 flex gap-8 mt-16">
        <div className="w-[384px] mb-[-40px]">
          <InputBlock
            label="Rule priority"
            className="mt-1"
            id="rulePriority"
            placeholder="Rule priority"
            onChange={e => onRuleInputChange(e.target.value)}
            formik={formik}
            description="Numerical value between 0 - 100, with 100 being the highest. This determines the order the rules are applied."
          />
        </div>
        <div className="w-[384px] mb-[-40px]">
          <InputBlock
            label="Point multiplier"
            className="mt-1"
            id="pointMultiplier"
            placeholder="Point multiplier"
            onChange={e => onRuleInputChange(e.target.value)}
            formik={formik}
            description="Earnings rate for points per dollar spent. Ex: enter 3 to set a 3x points earning rate."
          />
        </div>
      </div>
      {/* Category Values */}
      <div className="ml-3 flex gap-8 mt-16">
        <div className="w-[384px] mb-[-40px]">
          <Label htmlFor="category" className="text-[--gray-700] text-sm font-medium">
            Category
          </Label>
          <div className="border border-gray-200 rounded-lg mt-1">
            <Input
              placeholder="Search"
              value={searchCategories}
              onChange={e => onSearchChangeCategory(e.target.value)}
              className="w-[90%] text-[--gray-500] font-normal text-base mx-5 my-4"
            />
            {/* Handle Loading and Error States */}
            {isLoading ? (
              <div className="w-[384px]  pl-6 h-[200px]">Loading...</div>
            ) : isError ? (
              <div className="w-[384px] pl-6 h-[200px]">
                <p>Error loading categories: {error.message}</p>
                <button onClick={refetch}>Retry</button>
              </div>
            ) : (
              <ScrollArea className="w-[384px] overflow-y-auto relative px-5 py-0 sm:shadow-sm h-[200px]">
                {filteredCategoryValues.map(item => (
                  <div key={item.id} className="flex flex-row mb-3">
                    <Checkbox
                      isChecked={selectedCategoryItems.includes(item.description)}
                      onClick={() => handleCheckboxChange('category', item)}
                    />
                    <p className="w-[312px] h-auto font-medium text-[--gray-700 mt-[-1px] text-[#344054]">
                      {item.description}
                    </p>
                  </div>
                ))}
              </ScrollArea>
            )}
          </div>
          <p className="text-sm text-[--gray-600] font-normal mt-1 mb-2">
            Transactions type(s) that are eligible for earnings.
          </p>
          {/* {errors.selectedCategory && touched.selectedCategory && (
            <p className="mb-4 text-sm font-normal text-[#FF2C20]">Select least one value </p>
          )} */}
        </div>
        <div className="mb-2">
          <Label htmlFor="selectedCategory" className="text-[--gray-700] text-sm font-medium">
            Selected Category
          </Label>
          <ScrollArea className="w-[384px] overflow-y-auto relative p-3 border border-gray-200 bg-white rounded-lg sm:shadow-sm h-[274px] mt-1">
            {selectedCategoryItems.map(description => (
              <p key={description} className="w-[384px] h-[24px] mb-6 text-[--gray-500] font-normal text-base">
                {description}
              </p>
            ))}
          </ScrollArea>
        </div>
      </div>

      {/** Advanced options **/}
      <div className={`flex justify-center mt-10 ${showAdvanced ? 'mb-8' : 'mb-[-8px]'}`}>
        <button
          className="flex items-center text-sm text-[--gray-600] font-semibold underline underline-offset-4 mt-2"
          onClick={handleShowAdvanced}
        >
          <>
            <img src={showAdvanced ? Icons.minusIconBlack : Icons.plusIconBlack} alt="icon" className="mr-2 h-3 w-3" />
          </>
          Advanced options
        </button>
      </div>

      {showAdvanced ? (
        <div className="ml-3">
          <p className="ml- text-sm font-normal text-[#FF2C20] mt-1">
            The following option will require assistance from development to configure correctly
          </p>
          <div className="ml-0 flex flex-row gap-8 mt-6">
            <div className="w-[384px]">
              <DropDownContainer
                key={'optIn'}
                label="Opt in*"
                options={[
                  { value: 'true', label: 'true' },
                  { value: 'false', label: 'false' },
                ]}
                placeholder={'Select type'}
                id={'optIn'}
                formik={formik}
                value={formik.values.optIn}
              />
            </div>
            <div className="w-[384px]">
              <DropDownContainer
                key={'singleTransaction'}
                label="Single Transaction*"
                options={[
                  { value: 'true', label: 'true' },
                  { value: 'false', label: 'false' },
                ]}
                placeholder={'Select type'}
                id={'singleTransaction'}
                formik={formik}
                value={formik.values.singleTransaction}
              />
            </div>
          </div>
          <div className="ml-0 flex flex-row gap-8">
            {/*Types*/}
            <div className="w-[384px] mt-8">
              <InputBlock
                label="Event type*"
                className="mt-1"
                id="eventType"
                placeholder="Event type"
                autoComplete="given-name"
                formik={formik}
                onChange={e => onRuleInputChange(e.target.value)}
              />
            </div>
            <div className="w-[384px] mt-8">
              <InputBlock
                label="Ledger type*"
                className="mt-1"
                id="ledgerType"
                placeholder="Ledger type"
                autoComplete="given-name"
                formik={formik}
                onChange={e => onRuleInputChange(e.target.value)}
              />
            </div>
          </div>
          {/*Points*/}
          <div className="ml-0 flex flex-row gap-8">
            <div className="w-[384px] mt-8">
              <Label>Points required</Label>
              <DropDownContainer
                key={'pointsRequired'}
                options={[
                  { value: 'true', label: 'true' },
                  { value: 'false', label: 'false' },
                ]}
                placeholder={'Select type'}
                id={'pointsRequired'}
                formik={formik}
                value={formik.values.pointsRequired}
              />
            </div>
            <div className="w-[384px] mt-8">
              <InputBlock
                label="Maximum points amount"
                className="mt-1"
                id="maxPoints"
                placeholder="Maximum points amount"
                autoComplete="given-name"
                formik={formik}
                onChange={e => onRuleInputChange(e.target.value)}
              />
            </div>
          </div>
          {/*Counts*/}
          <div className="ml-0 flex flex-row gap-8">
            <div className="w-[384px] mt-8">
              <InputBlock
                label="Minimum count"
                className="mt-1"
                id="minimumCount"
                placeholder="Minimum count"
                autoComplete="given-name"
                formik={formik}
                onChange={e => onRuleInputChange(e.target.value)}
              />
            </div>
            <div className="w-[384px] mt-8">
              <InputBlock
                label="Maximum count"
                className="mt-1"
                id="maxCount"
                placeholder="Maximum count"
                autoComplete="given-name"
                formik={formik}
                onChange={e => onRuleInputChange(e.target.value)}
              />
            </div>
          </div>
          {/*Minimum spend & Fixed points */}
          <div className="ml-0 flex flex-row gap-8">
            <div className="w-[384px] mt-8">
              <InputBlock
                label="Minimum spend"
                className="mt-1"
                id="minimumSpend"
                placeholder="Minimum spend"
                autoComplete="given-name"
                formik={formik}
                onChange={e => onRuleInputChange(e.target.value)}
              />
            </div>
            <div className="w-[384px] mt-8">
              <InputBlock
                label="Fixed points"
                className="mt-1"
                id="fixedPoints"
                placeholder="Fixed points"
                autoComplete="given-name"
                formik={formik}
                onChange={e => onRuleInputChange(e.target.value)}
              />
            </div>
          </div>
          {/*Display score & Days after activation */}
          <div className="ml-0 flex flex-row gap-8">
            <div className="w-[384px] mt-8">
              <InputBlock
                label="Display score"
                className="mt-1"
                id="displayScore"
                placeholder="Display score"
                autoComplete="given-name"
                formik={formik}
                onChange={e => onRuleInputChange(e.target.value)}
              />
            </div>
            <div className="w-[384px] mt-8">
              <InputBlock
                label="Days after activation"
                className="mt-1"
                id="daysAfterActivation"
                placeholder="Days after activation"
                autoComplete="given-name"
                formik={formik}
                onChange={e => onRuleInputChange(e.target.value)}
              />
            </div>
          </div>
          {/*MCC*/}
          <div className="ml-0 flex flex-row gap-8">
            <div className="w-[384px] mt-8">
              <InputBlock
                label="MCC inclusion list"
                className="mt-1"
                id="mccInclusionList"
                placeholder="MCC inclusion list"
                autoComplete="given-name"
                formik={formik}
                onChange={e => onRuleInputChange('mccInclusionList', e.target.value)}
              />
            </div>
            <div className="w-[384px] mt-8">
              <InputBlock
                label="MCC exclusion list"
                className="mt-1"
                id="mccExclusionList"
                placeholder="MCC exclusion list"
                autoComplete="given-name"
                formik={formik}
                onChange={e => onRuleInputChange('mccExclusionList', e.target.value)}
              />
            </div>
          </div>
          {/*MID*/}
          <div className="ml-0 flex flex-row gap-8">
            <div className="w-[384px] mt-8">
              <InputBlock
                label="MID inclusion list"
                className="mt-1"
                id="midInclusionList"
                placeholder="MID inclusion list"
                autoComplete="given-name"
                formik={formik}
                onChange={e => onRuleInputChange('midInclusionList', e.target.value)}
              />
            </div>
            <div className="w-[384px] mt-8">
              <InputBlock
                label="MID exclusion list"
                className="mt-1"
                id="midExclusionList"
                placeholder="MID exclusion list"
                autoComplete="given-name"
                formik={formik}
                onChange={e => onRuleInputChange('midExclusionList', e.target.value)}
              />
            </div>
          </div>
          {/*CID*/}
          <div className="ml-0 flex flex-row gap-8">
            <div className="w-[384px] mt-8">
              <InputBlock
                label="Chain inclusion list"
                className="mt-1"
                id="chainInclusionList"
                placeholder="Chain inclusion list"
                autoComplete="given-name"
                formik={formik}
                onChange={e => onRuleInputChange('chainInclusionList', e.target.value)}
              />
            </div>
            <div className="w-[384px] mt-8">
              <InputBlock
                label="Chain exclusion list"
                className="mt-1"
                id="chainExclusionList"
                placeholder="Chain exclusion list"
                autoComplete="given-name"
                formik={formik}
                onChange={e => onRuleInputChange('chainExclusionList', e.target.value)}
              />
            </div>
          </div>
          {/*Online & Wallet*/}
          <div className="ml-0 flex flex-row gap-8">
            <div className="w-[384px] mt-8">
              <Label>Online</Label>
              <DropDownContainer
                key={'online'}
                options={[
                  { value: 'online', label: 'online' },
                  { value: 'in-store', label: 'in-store' },
                ]}
                placeholder={'Select type'}
                id={'online'}
                formik={formik}
                value={formik.values.online}
              />
            </div>
            <div className="w-[384px] mt-8">
              <InputBlock
                label="Wallet"
                className="mt-1"
                id="wallet"
                placeholder="Wallet"
                autoComplete="given-name"
                formik={formik}
                onChange={e => onRuleInputChange('wallet', e.target.value)}
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

RulesForm.propTypes = {
  searchCategories: PropTypes.string,
  onRuleInputChange: PropTypes.func.isRequired,
  onCheckboxChange: PropTypes.func.isRequired,
  onSearchChangeCategory: PropTypes.func.isRequired,
  groupOptions: PropTypes.array.isRequired,
  formik: PropTypes.object.isRequired,
}

export default RulesForm
